import { useInfiniteQuery } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import type { ApiError } from '@/api/errors'
import { Banner } from '@/components/Banner'
import { RecordTable, type RecordColumn } from '@/components/RecordTable'
import { Card } from '@/components/Card'
import { Page, PageHeader } from '@/components/Page'
import { EmptyState } from '@/components/EmptyState'
import { Icon } from '@/components/Icon'
import { Spinner } from '@/components/Spinner'
import { TextField } from '@/components/TextField'
import { formatIdentityLine } from '@/lib/age'
import { formatVisitDate } from '@/lib/dates'
import { insights, type PrescriptionRow } from './api'
import { errorMessage } from '@/api/messages'
import { groupByVisit } from '@/lib/prescriptions'
import { useDebounced } from '@/lib/useDebounced'
import { useInfiniteScroll } from '@/lib/useInfiniteScroll'
import { queryKeys } from '@/api/queryKeys'
import type { Paginated } from '@/api/types'

/** Consultations per page, not medicines — one prescription is never split
 *  across two of them. */
const PAGE = 20

/** Drug first — it is what the row is about, and what the filter searches. */
const PRESCRIPTION_COLUMNS: RecordColumn<PrescriptionRow>[] = [
  {
    key: 'drug',
    header: 'Medicine',
    primary: true,
    className: 'font-semibold text-fg',
    cell: (row) => row.drug_name,
  },
  { key: 'dose', header: 'Dose', cell: (row) => row.dose },
  { key: 'frequency', header: 'Frequency', cell: (row) => row.frequency },
  { key: 'duration', header: 'Duration', cell: (row) => row.duration },
  { key: 'remarks', header: 'Remarks', cell: (row) => row.instructions },
]

/** Every prescription the clinic has written, searchable by drug — "who is on
 *  Amlodipine?" when one is recalled. */
export function PrescriptionsScreen() {
  const [drug, setDrug] = useState('')
  const debounced = useDebounced(drug.trim())
  const sentinel = useRef<HTMLDivElement>(null)

  const list = useInfiniteQuery<Paginated<PrescriptionRow>, ApiError>({
    queryKey: queryKeys.prescriptions(debounced),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam, signal }) =>
      insights.prescriptions(debounced, PAGE, pageParam as string | null, signal),
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  })
  const rows = list.data?.pages.flatMap((page) => page.data) ?? []
  // Only on the very first load. A refetch while typing keeps the old rows, and
  // swapping them for a spinner every keystroke is what makes a search feel
  // broken rather than fast.
  const loading = list.isLoading

  useInfiniteScroll(sentinel, {
    hasMore: Boolean(list.hasNextPage),
    loading: list.isFetchingNextPage,
    onLoadMore: () => void list.fetchNextPage(),
  })

  // Safe across the page boundary: the server ends every page on a whole
  // consultation, so a run never resumes at the start of the next page.
  const groups = groupByVisit(rows, (row) => row.visit_id)
  const total = list.data?.pages[0]?.total

  return (
    <Page fill>
      <PageHeader
        eyebrow="Clinic"
        title="Prescriptions"
        description="Everything prescribed across the clinic."
      />

      <div className="mb-4 max-w-96">
        <TextField
          label="Filter by medicine"
          placeholder="Amlodipine"
          type="search"
          autoComplete="off"
          value={drug}
          onChange={(e) => setDrug(e.target.value)}
        />
      </div>

      {list.error && (
        <Banner tone={list.error.isNetwork ? 'warning' : 'danger'}>
          {errorMessage(list.error)}
        </Banner>
      )}

      {/* The clinic's total, sent with the first page. Counting the loaded rows
          would count how far the doctor scrolled. */}
      <Card title={`${total ?? 0} ${total === 1 ? 'prescription' : 'prescriptions'}`} flush fill>
        {loading ? (
          <Spinner label="Loading prescriptions" />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<Icon name="pill" size="2rem" />}
            title={
              debounced ? `Nothing prescribed matching "${debounced}"` : 'No prescriptions yet'
            }
          />
        ) : (
          <>
            {/* One group per consultation. The patient and date move into the
                group heading rather than repeating on every medicine, which is
                also what stops two same-day visits reading as one. */}
            <RecordTable
              caption={`${total ?? 0} prescriptions`}
              columns={PRESCRIPTION_COLUMNS}
              rowKey={(row) => `${row.visit_id}-${row.position}`}
              groups={groups.map((group) => {
                const first = group.items[0]!
                return {
                  id: group.visitId,
                  rows: group.items,
                  heading: (
                    <>
                      <Link className="font-semibold" to={`/patients/${first.patient_id}`}>
                        {first.full_name}
                      </Link>
                      <span className="text-fg-subtle">
                        {' · '}
                        {first.op_number} · {formatIdentityLine(first.age, first.sex)}
                        {' · '}
                        {formatVisitDate(first.visit_date)}
                      </span>
                    </>
                  ),
                }
              })}
            />

            {/* Watched, not clicked: crossing it loads the next page. */}
            <div ref={sentinel} className="flex min-h-10 items-center justify-center p-3">
              {list.isFetchingNextPage && <Spinner label="Loading more prescriptions" />}
              {!list.hasNextPage && groups.length > PAGE && (
                <p className="text-xs text-fg-subtle">That is everything prescribed.</p>
              )}
            </div>
          </>
        )}
      </Card>
    </Page>
  )
}
